import { Stack } from '@mui/system';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import CircularProgress from '@mui/material/CircularProgress';

import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';

import { useSnackbar } from 'notistack';

import CardButton from 'components/CardButton';
import Route from 'routes/Route';
import { useModal } from 'hooks/useModal';
import { useUser } from 'hooks/useUser';

import { UserType } from 'routes/Dashboard';

import EditEvolutionModal from 'routes/Evolution/EditEvolutionModal';

import styles from './styles';

type PacientMenuState = {
  id?: string | number;
  name?: string;
} | null;

const PacientMenu = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const rawState = location.state as PacientMenuState;
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const { isOpen, handleOpen, handleClose } = useModal();

  const pacientData = useMemo(() => {
    if (
      rawState?.id == null ||
      rawState.name == null ||
      String(rawState.name).trim() === ''
    ) {
      return null;
    }
    return {
      id: Number(rawState.id),
      name: String(rawState.name),
    };
  }, [rawState]);

  useEffect(() => {
    if (pacientData != null) {
      return;
    }
    enqueueSnackbar('Selecione um paciente na lista.', { variant: 'info' });
    const fallback =
      user?.type === UserType.PROFESSIONAL ? Route.PACIENT : Route.DASHBOARD;
    navigate(fallback, { replace: true });
  }, [enqueueSnackbar, navigate, pacientData, user?.type]);

  const navigationState = useMemo(
    () =>
      pacientData != null
        ? { id: pacientData.id, name: pacientData.name }
        : undefined,
    [pacientData],
  );

  const goToEvolution = useCallback(() => {
    if (navigationState == null) {
      return;
    }
    navigate(Route.EVOLUTION, { state: navigationState });
  }, [navigate, navigationState]);

  const goToReport = useCallback(() => {
    if (navigationState == null) {
      return;
    }
    navigate(Route.REPORT, { state: navigationState });
  }, [navigate, navigationState]);

  if (pacientData == null) {
    return (
      <Stack sx={styles.container}>
        <Stack sx={styles.loadingBox}>
          <CircularProgress aria-label="Redirecionando" />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack sx={styles.container}>
      <CardButton
        icon={<EditNoteIcon />}
        text="Editar Informacoes"
        to={goToReport}
      />
      <CardButton
        icon={<TrendingUpIcon />}
        text="Adicionar Evolucao"
        to={handleOpen}
      />
      <CardButton
        icon={<QueryStatsIcon />}
        text="Mostrar Evolucao"
        to={goToEvolution}
      />
      <EditEvolutionModal
        isOpen={isOpen}
        handleClose={handleClose}
        pacientData={pacientData}
      />
    </Stack>
  );
};

export default PacientMenu;
